
import React from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import ProcessDashboard from './components/ProcessDashboard';
import NewProcessForm from './components/NewProcessForm';
import Login from './components/Login';
import AdminPanel from './components/AdminPanel';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

const Settings: React.FC = () => (
  <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 max-w-4xl">
    <h3 className="font-serif text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Global Configuration</h3>
    <div className="space-y-8">
      <section>
        <h4 className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4 border-b border-slate-100 dark:border-slate-800 pb-2">Automation</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
           <div className="p-4 bg-slate-50 dark:bg-slate-950/50 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div>
                <p className="font-bold text-sm text-slate-800 dark:text-slate-200">SMS Reminders</p>
                <p className="text-xs text-slate-500">24h and 15m notifications</p>
              </div>
              <div className="w-10 h-5 bg-amber-600 rounded-full relative"><div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full"></div></div>
           </div>
                          <div className="p-4 bg-slate-50 dark:bg-slate-950/50 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-sm text-slate-800 dark:text-slate-200">Email Reminders</p>
                    <p className="text-xs text-slate-500">24h and 15m notifications</p>
                  </div>
                  <div className="w-10 h-5 bg-amber-600 rounded-full relative"><div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full"></div></div>
                </div>
           <div className="p-4 bg-slate-50 dark:bg-slate-950/50 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div>
                <p className="font-bold text-sm text-slate-800 dark:text-slate-200">AI Transcription</p>
                <p className="text-xs text-slate-500">Enable audio logs</p>
              </div>
              <div className="w-10 h-5 bg-amber-600 rounded-full relative"><div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full"></div></div>
           </div>
        </div>
      </section>
      
      <section>
        <h4 className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4 border-b border-slate-100 dark:border-slate-800 pb-2">System Models</h4>
        <div className="p-4 bg-slate-50 dark:bg-slate-950/50 rounded-2xl border border-slate-100 dark:border-slate-800">
           <div className="flex justify-between items-center mb-4">
              <p className="font-bold text-sm text-slate-800 dark:text-slate-200">Primary Reasoning Engine</p>
              <span className="text-[10px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-500 px-2 py-0.5 rounded">GEMINI 3 PRO</span>
           </div>
           <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">System is configured to use high-reasoning Gemini models for legal drafting to ensure compliance with Lluislaw professional standards.</p>
        </div>
      </section>
    </div>
  </div>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={
        <ProtectedRoute>
          <Layout>
            <ProcessDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/new" element={
        <ProtectedRoute>
          <Layout>
            <NewProcessForm />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Layout>
            <AdminPanel />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout>
            <Settings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <HashRouter>
          <AppRoutes />
        </HashRouter>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
